import { useEffect, useRef, useState } from "react";
import { Send, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API } from "@/lib/api";
import { streamSSE } from "@/lib/deep";
import { Md } from "@/components/cast/AIHelper";
import { ScriptureRef } from "@/components/cast/Common";
import { ChapterQuiz } from "./ChapterQuiz";

export const GUIDE_ICON = "/jesus-guide.jpg";

function Exchange({ it, i }: { it: any; i: number }) {
  return (
    <div className="space-y-3" data-testid={`guide-exchange-${i}`}>
      <p className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-[#1E293B] px-4 py-2.5 text-sm text-amber-50">{it.question}</p>
      <div className="flex gap-2.5">
        <img src={GUIDE_ICON} alt="" className="mt-1 h-7 w-7 shrink-0 rounded-full object-cover" />
        <div className="min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-[#E2DBCE] bg-white p-4 text-sm">
          {it.error ? <p className="text-red-700" data-testid="guide-error">{it.error}</p> : it.answer ? <Md text={it.answer} /> : <p className="animate-pulse text-slate-500">Reading the passage…</p>}
          {it.done && !it.error && (it.refs?.length ? (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-[#E2DBCE] pt-3" data-testid="guide-citations">{it.refs.map((r: string) => <ScriptureRef key={r} refText={r} />)}</div>
          ) : <p className="mt-3 flex gap-2 rounded-lg bg-red-50 p-2 text-xs text-red-800" data-testid="guide-uncited"><ShieldAlert className="h-4 w-4 shrink-0" />No scripture citation found. Treat this as unverified.</p>)}
        </div>
      </div>
    </div>
  );
}

export function Guide({ open, onOpenChange, book, ch, name, prompt }: { open: boolean; onOpenChange: (v: boolean) => void; book: number; ch: number; name: string; prompt: string }) {
  const [items, setItems] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { setItems([]); api.get(`/deep/guide/${book}/${ch}`).then((r) => setItems(r.data.map((h: any) => ({ ...h, done: true })))).catch(() => {}); }, [book, ch]);
  useEffect(() => { if (prompt) setQ(prompt); }, [prompt]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [items]);
  const patch = (fn: (a: any) => any) => setItems((p) => [...p.slice(0, -1), fn(p[p.length - 1])]);
  const ask = async (question: string) => {
    if (question.trim().length < 3 || busy) return;
    setBusy(true); setQ("");
    setItems((p) => [...p, { question, answer: "", done: false }]);
    try {
      await api.get("/auth/me");
      await streamSSE(`${API}/deep/guide`, { book, ch, question }, (ev) => {
        if (ev.t === "delta") patch((a) => ({ ...a, answer: a.answer + ev.c }));
        else if (ev.t === "done") patch((a) => ({ ...a, done: true, refs: ev.refs }));
        else if (ev.t === "error") patch((a) => ({ ...a, done: true, error: ev.message }));
      });
    } finally { setBusy(false); }
  };
  const starters = [`What is happening in ${name} ${ch}?`, `What is the key verse in ${name} ${ch}?`, "How can I live this out today?"];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 bg-[#FBF7EF] p-0 sm:max-w-md" data-testid="guide-sheet">
        <SheetHeader className="border-b border-[#E2DBCE] p-5 text-left">
          <div className="flex items-center gap-3">
            <img src={GUIDE_ICON} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-300" />
            <div><SheetTitle className="font-display text-2xl">Study Guide</SheetTitle><SheetDescription className="text-xs">{name} {ch} · answers cite Scripture. A study aid, not Jesus himself.</SheetDescription></div>
          </div>
        </SheetHeader>
        <Tabs defaultValue="ask" className="flex min-h-0 flex-1 flex-col">
          <TabsList className="mx-5 mt-4 bg-[#F3ECE0]"><TabsTrigger value="ask" data-testid="guide-tab-ask">Ask</TabsTrigger><TabsTrigger value="quiz" data-testid="guide-tab-quiz">Quiz me</TabsTrigger></TabsList>
          <TabsContent value="ask" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
            <div className="flex-1 space-y-5 overflow-y-auto p-5">
              {!items.length && <div className="flex flex-wrap gap-2">{starters.map((s, i) => <button key={s} data-testid={`guide-starter-${i}`} onClick={() => ask(s)} className="chip text-left hover:bg-[#E2DBCE]">{s}</button>)}</div>}
              {items.map((it, i) => <Exchange key={i} it={it} i={i} />)}
              <div ref={end} />
            </div>
            <div className="flex gap-2 border-t border-[#E2DBCE] bg-white p-3">
              <Textarea data-testid="guide-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about this chapter…" className="min-h-[48px] resize-none border-0 shadow-none focus-visible:ring-0"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(q); } }} />
              <Button data-testid="guide-send" disabled={busy || q.trim().length < 3} onClick={() => ask(q)} className="h-auto rounded-xl bg-[#C86D51] hover:bg-[#B35A3F]"><Send className="h-4 w-4" /></Button>
            </div>
          </TabsContent>
          <TabsContent value="quiz" className="mt-0 min-h-0 flex-1 overflow-y-auto p-5 data-[state=inactive]:hidden"><ChapterQuiz book={book} ch={ch} /></TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
